import { nanoid } from "nanoid";
import type { TItem, TCurve, TSelectors, TEntry } from "../types";
import type { Product_Config_Insert_Input, ProductInfoFragmentFragment } from "../../../__generated__/gql/graphql";
import { defaultFieldNameSelector } from "@package/grid-utils/products";
import type { TSelectorExchange } from "../../market-grid/selectorsData";
import { getSelectors } from "../../menu-system/configs/sections/field";
import { CURVE_MANAGEMENT_MAX_CELLS, CURVE_MANAGEMENT_MAX_PERIODS } from "../constants";

export { CURVE_MANAGEMENT_MAX_CELLS, CURVE_MANAGEMENT_MAX_PERIODS };

/**
 * Folder ordering for the curve search dropdown: exchange first, then
 * organisation, then EOD, then any unknown source last. Mirrors
 * useProductOptions (curve-management/hooks.ts) for the source-type
 * inference (explicit `source_type` first, then exchange/organisation/EOD
 * heuristics).
 */
export function getCurvePackageSourceSortRank(curve: TCurve): number {
  const src = curve.packageByPackage?.sourceBySource;
  let sourceType = src?.source_type ?? undefined;
  const sourceName = src?.name ?? "";
  if (!sourceType) {
    const hasExchange = src?.exchange !== null && src?.exchange !== undefined;
    if (hasExchange) {
      sourceType = "exchange";
    } else if (sourceName.toLowerCase().includes("artis")) {
      sourceType = "organisation";
    } else {
      sourceType = "eod";
    }
  }
  const t = String(sourceType).toLowerCase();
  if (t === "exchange") return 0;
  if (t === "organisation") return 1;
  if (t === "eod") return 2;
  return 3;
}

/**
 * Product search in the formula bar: every whitespace-separated keyword must match as a
 * substring somewhere in the combined haystack (case-insensitive), so e.g. "brent fut"
 * matches name/description like "BRT FT … Brent Crude Fut".
 */
export function haystackMatchesKeywordQuery(haystack: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = haystack.toLowerCase();
  for (const kw of q.split(/\s+/)) {
    if (!kw) continue;
    if (!hay.includes(kw)) return false;
  }
  return true;
}

/** Searchable text for a curve: name, description, folder/package. */
export function curveProductSearchHaystack(curve: TCurve): string {
  return [curve.name, curve.description ?? "", curve.packageByPackage?.name ?? ""].join(" ").trim();
}

export function curveMatchesProductSearchQuery(curve: TCurve, query: string): boolean {
  return haystackMatchesKeywordQuery(curveProductSearchHaystack(curve), query);
}

/** Sort package/folder keys by source type (exchange → organisation → EOD), then alphabetically. */
export function sortCurvePackageGroupKeys(groups: Record<string, TCurve[]>): string[] {
  return Object.keys(groups).sort((a, b) => {
    const ca = groups[a][0];
    const cb = groups[b][0];
    if (!ca || !cb) return a.localeCompare(b);
    const ra = getCurvePackageSourceSortRank(ca);
    const rb = getCurvePackageSourceSortRank(cb);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

/** Group by package name, order folders as {@link sortCurvePackageGroupKeys}, curves A–Z within each folder. */
export function sortCurvesGroupedByPackage(curves: TCurve[]): TCurve[] {
  const groups: Record<string, TCurve[]> = {};
  for (const c of curves) {
    const pkg = c.packageByPackage?.name || "";
    if (!groups[pkg]) groups[pkg] = [];
    groups[pkg].push(c);
  }
  const sortedKeys = sortCurvePackageGroupKeys(groups);
  return sortedKeys.flatMap((pkg) => groups[pkg].slice().sort((a, b) => a.name.localeCompare(b.name)));
}

/** Plain-text formula fragment for a curve reference (matches builder / parseFormula token shape). */
export function buildFormulaSnippetForCurveAndField(product: TCurve, fieldSelectorValue: string): string {
  const sel = fieldSelectorValue.startsWith(".") ? fieldSelectorValue : `.${fieldSelectorValue}`;
  return `${curveBracket(product)}${sel}`;
}

export function parseItems(items: TItem[]): string {
  return items.reduce((acc, item) => {
    if (item.type === "parenthesis") {
      return `${acc}${item.value}`;
    }

    if (item.type === "operator") {
      return `${acc} ${item.value} `;
    }

    if (item.type === "product") {
      return `${acc}${item.value}`;
    }

    if (item.type === "selector") {
      return `${acc}${item.value}`;
    }

    // In the formula builder, relative months are shown as [-4], [0], or [+12].
    // In raw formulas, ^ denotes next relative month and ' denotes previous
    // relative month. So [+3] becomes ^^^, 0 is nothing, and [-4] becomes ''''.
    if (item.type === "month") {
      const monthValue = Number(item.value);
      if (monthValue === 0) {
        return acc;
      } else if (monthValue > 0) {
        return `${acc}${"^".repeat(Math.abs(monthValue))}`;
      } else {
        return `${acc}${"'".repeat(Math.abs(monthValue))}`;
      }
    }

    if (item.type === "number") {
      return `${acc}${item.value}`;
    }

    return acc;
  }, "");
}

export function parseFormula(products: TCurve[], selectors: TSelectors, formulaString: string): TItem[] {
  const formula = formatFormulaOperators(formulaString);
  const items: TItem[] = [];

  const tokenRegex =
    /\[(.+?)\s*\(([^()]*(?:\([^()]*\)[^()]*)*)\):\s*([^\]]+)\]|\[([^\]]+)\]|(\.[A-Za-z0-9_]+(?:[.-][A-Za-z0-9_]+)*)|(-\d+(?:\.\d+)?)|([+\-*/])|(\^+|'+)|(\()|(\))|(\d+(\.\d+)?)/g;

  let match: RegExpExecArray | null;

  match = tokenRegex.exec(formula);
  while (match !== null) {
    if (match[1] && match[2] && match[3]) {
      // Product — full [name (desc): pkg] format
      const name = removeSpaces(match[1]);
      const description = removeSpaces(match[2]);
      const packageName = removeSpaces(match[3]);

      const product = products.find((p) => matchesCurveBracketParts(p, name, description, packageName));

      if (!product) {
        match = tokenRegex.exec(formula);
        continue;
      }

      items.push({
        itemId: nanoid(),
        type: "product",
        value: curveBracket(product),
        id: product.id,
      });
    } else if (match[4]) {
      // Fallback bracket — [rawId] or unrecognised [...]
      const rawId = match[4].trim();
      const product = products.find((p) => p.id === rawId);

      if (product) {
        // Found by ID — build the full display value
        items.push({
          itemId: nanoid(),
          type: "product",
          value: curveBracket(product),
          id: product.id,
        });
      } else {
        // Product not in the available list — show as an unresolved pill so it
        // remains visible rather than disappearing from the formula.
        items.push({
          itemId: nanoid(),
          type: "product",
          value: `[${rawId}]`,
          id: rawId,
        });
      }
    } else if (match[5]) {
      // Selector
      const last = items[items.length - 1];

      if (last && last.type === "product") {
        const productSelectors = selectors.find((s) => s.id === last.id || s.product === last.value)?.selectors || [];

        // Only validate selector existence when we have selector metadata for this product.
        // For unresolved products (not in the available list), productSelectors is empty so
        // we allow any selector through rather than silently dropping it.
        if (productSelectors.length > 0) {
          const selectorExists = productSelectors.some((s) => s.value === match?.[5].slice(1));

          if (!selectorExists) {
            match = tokenRegex.exec(formula);
            continue;
          }
        }
      }

      items.push({
        itemId: nanoid(),
        type: "selector",
        value: match[5],
      });
    } else if (match[6]) {
      // Negative number literal (e.g. -1, -0.5)
      items.push({
        itemId: nanoid(),
        type: "number",
        value: match[6],
      });
    } else if (match[7]) {
      // Operator
      items.push({
        itemId: nanoid(),
        type: "operator",
        value: match[7],
      });
    } else if (match[8]) {
      // Month
      const monthStr = match[8];
      const monthValue = monthStr.startsWith("^") ? monthStr.length : -monthStr.length;

      items.push({
        itemId: nanoid(),
        type: "month",
        value: monthValue,
      });
    } else if (match[9]) {
      // Opening parenthesis
      items.push({
        itemId: nanoid(),
        type: "parenthesis",
        value: "(",
      });
    } else if (match[10]) {
      // Closing parenthesis
      items.push({
        itemId: nanoid(),
        type: "parenthesis",
        value: ")",
      });
    } else if (match[11]) {
      // Positive number literal
      items.push({
        itemId: nanoid(),
        type: "number",
        value: match[11],
      });
    }

    match = tokenRegex.exec(formula);
  }

  return items;
}

// Removes double spaces from a string.
export function removeSpaces(str: string, noTrim?: boolean): string {
  const result = str.replace(/\s+/g, " ");
  return noTrim ? result : result.trim();
}

/** The fields a curve contributes to its formula display bracket. */
type TCurveDisplayFields = {
  name: string;
  description?: string | null;
  packageByPackage?: { name?: string | null } | null;
};

/**
 * Canonical `[name (description): package]` token for a curve.
 *
 * Whitespace runs are collapsed because `formatFormulaOperators` collapses them in the
 * formula string too — a curve whose stored name/description/package contains a double
 * space, tab or non-breaking space would otherwise never match itself when parsed back.
 * Every producer and consumer of the token must go through here.
 */
export function curveBracket(product: TCurveDisplayFields): string {
  return removeSpaces(`[${product.name} (${product.description ?? ""}): ${product.packageByPackage?.name ?? ""}]`);
}

/** Matches a curve against display-bracket parts that have already been whitespace-collapsed. */
function matchesCurveBracketParts(
  product: TCurveDisplayFields,
  name: string,
  description: string,
  packageName: string,
): boolean {
  return (
    removeSpaces(product.name) === name &&
    removeSpaces(product.description ?? "") === description &&
    removeSpaces(product.packageByPackage?.name ?? "") === packageName
  );
}

export function formatFormulaOperators(formulaString: string): string {
  // Add spaces around operators outside square brackets and selector names,
  // then strip spaces immediately adjacent to parentheses so round-trips are
  // stable. Selector names may include hyphens/dots (e.g. `.pricing-days`).
  return formulaString
    .replace(/\[.*?\]|[+\-*/]/g, (match, offset, full) => {
      if (match.startsWith("[")) return match;

      if (match === "-") {
        const previousDelimiter = Math.max(
          full.lastIndexOf(" ", offset - 1),
          full.lastIndexOf("+", offset - 1),
          full.lastIndexOf("-", offset - 1),
          full.lastIndexOf("*", offset - 1),
          full.lastIndexOf("/", offset - 1),
          full.lastIndexOf("(", offset - 1),
          full.lastIndexOf(")", offset - 1),
          full.lastIndexOf("]", offset - 1),
        );
        const tokenStart = previousDelimiter + 1;
        const tokenBeforeOperator = full.slice(tokenStart, offset);
        const next = full[offset + 1];
        if (tokenBeforeOperator.startsWith(".") && /[A-Za-z0-9_]/.test(next ?? "")) return match;

        // Treat '-digit' (no space) as a negative number literal when in a unary
        // position — i.e. at the start, or immediately after a space, operator, or '('.
        // Per the agreed parsing rule: '-1' = negative number, '- 1' = binary subtraction.
        const charBefore = offset > 0 ? full[offset - 1] : undefined;
        const isUnaryPosition = charBefore === undefined || /[\s+\-*/]/.test(charBefore) || charBefore === "(";
        if (isUnaryPosition && /\d/.test(next ?? "")) return match;
      }

      return ` ${match} `;
    })
    .replace(/\s+/g, " ")
    .replace(/\(\s+/g, "(")
    .replace(/\s+\)/g, ")")
    .trim();
}

/** True if a product pill references `selfCurveId` without a non-zero relative month after its selector(s). */
export function hasSelfReferenceWithoutMonthOffset(items: TItem[], selfCurveId: string | undefined): boolean {
  if (!selfCurveId) return false;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type !== "product" || item.id !== selfCurveId) continue;
    let j = i + 1;
    while (j < items.length && items[j].type === "selector") j += 1;
    const next = items[j];
    if (next?.type === "month" && Number(next.value) !== 0) continue;
    return true;
  }
  return false;
}

/** Round-trip OK and, when `selfCurveId` is set, no bare self-reference. */
export function validateFormula(
  formulaString: string,
  products: TCurve[],
  selectors: TSelectors,
  selfCurveId?: string,
): boolean {
  const formula = formatFormulaOperators(formulaString);
  const items = parseFormula(products, selectors, formula);
  const parsedString = parseItems(items);
  if (parsedString !== formula) return false;
  if (selfCurveId && hasSelfReferenceWithoutMonthOffset(items, selfCurveId)) return false;
  return true;
}

export function adjustFormulaString(formulaString: string): string {
  // Ensure no multiple spaces
  const withoutExtraSpaces = removeSpaces(formulaString, true);
  return withoutExtraSpaces;
}

const PRODUCT_BRACKET_RE = /\[(.+?)\s*\(([^()]*(?:\([^()]*\)[^()]*)*)\):\s*([^\]]+)\]|\[([^\]]+)\]/g;

// Replaces product display brackets with product ids.
export function replaceFormulaProducts(formulaString: string, products: TCurve[]) {
  return formulaString.replace(PRODUCT_BRACKET_RE, (full, name, desc, pkg) => {
    if (name === undefined || desc === undefined || pkg === undefined) {
      // Already a raw `[id]` (or unrecognised) — leave untouched.
      return full;
    }
    const normalisedName = removeSpaces(name);
    const normalisedDesc = removeSpaces(desc);
    const normalisedPkg = removeSpaces(pkg);
    const product = products.find((p) => matchesCurveBracketParts(p, normalisedName, normalisedDesc, normalisedPkg));
    return product ? `[${product.id}]` : full;
  });
}

// Replaces product IDs in formula string with their corresponding full product strings.
// i.e. "[product-id]" becomes "[Product A (Desc): Package]"
export function replaceFormulaIds(formulaString: string, products: TCurve[]) {
  let adjustedFormula = removeSpaces(formulaString);
  products.forEach((p) => {
    const productString = curveBracket(p);
    const productIdString = `[${p.id}]`;
    adjustedFormula = adjustedFormula.split(productIdString).join(productString);
  });
  return adjustedFormula;
}

// Converts entries to product config objects for saving.
// The index of an entry corresponds to the relative_month in the product config.
// entry.formula is the formula for that relative month.
// Each entry has a "repetitions" field. So if an entry has repetitions = 3, it means that the same formula applies for that month and the next 2 months.
// When repeatFinalBlock is true, the last entry's formula is extended to fill up to
// CURVE_MANAGEMENT_MAX_PERIODS months total.
export function entriesToProductConfigs(
  entries: TEntry[],
  curveId: string,
  products: TCurve[],
  repeatFinalBlock = false,
) {
  const MAX_MONTHS = CURVE_MANAGEMENT_MAX_PERIODS;
  const productConfigs: Array<Product_Config_Insert_Input> = [];
  let relativeMonth = 0;

  entries.forEach((entry, index) => {
    // Skip ahead by any leading gap so configs preserve the original
    // offsets when a curve has empty months between blocks (e.g. 0–4 and 11–47).
    relativeMonth += Math.max(0, entry.gapBefore ?? 0);

    const isLast = index === entries.length - 1;
    const reps =
      isLast && repeatFinalBlock ? Math.max(entry.repetitions, MAX_MONTHS - relativeMonth) : entry.repetitions;

    for (let i = 0; i < reps; i++) {
      const rawFormula = entry.items.length > 0 ? parseItems(entry.items) : entry.formula;
      productConfigs.push({
        product: curveId,
        relative_month: relativeMonth + i,
        formula: entry.manualEntry ? null : replaceFormulaProducts(rawFormula, products),
      });
    }

    relativeMonth += reps;
  });

  return productConfigs;
}

export function generateDeltaDayProductConfigs(baseCurveId: string, deltaDayCurveId: string, eodProductDep: string) {
  const productConfigs: Array<Product_Config_Insert_Input> = [];

  for (let i = 0; i < 36; i++) {
    productConfigs.push({
      product: deltaDayCurveId,
      relative_month: i,
      formula: `[${baseCurveId}].value - [${eodProductDep}].value`,
    });
  }

  return productConfigs;
}

export function getInputCurveSelectors(
  curveInputProducts: ProductInfoFragmentFragment[] | undefined,
): TSelectors | undefined {
  return curveInputProducts?.flatMap((p) => {
    const defaultSelector = defaultFieldNameSelector(p.artis_type) as string;
    const exchange = p.packageByPackage?.sourceBySource?.exchange?.code as TSelectorExchange;
    const maturity = p.maturity;
    const hideValue = p.artis_type === "customer_curve" || p.artis_type === "eod";
    const product = curveBracket(p);

    if (!exchange || !maturity) {
      return [
        {
          id: p.id,
          product,
          selectors: defaultSelector ? [{ label: defaultSelector, value: defaultSelector, hidden: hideValue }] : [],
        },
      ];
    }

    const rawSelectors = getSelectors({ exchange, maturity, productId: p.id });

    if (rawSelectors.length === 0 && defaultSelector) {
      rawSelectors.push({ label: defaultSelector, value: defaultSelector, groupValue: "" });
    }

    // Strip groupValue and add hidden flag, then sort with "fv" and "value" first.
    const normalised = rawSelectors.map((s) => ({
      label: s.label,
      value: s.value,
      hidden: hideValue && s.value === "value",
    }));

    const sorted = normalised.sort((a, b) => {
      if (a.value === "fv") return -1;
      if (b.value === "fv") return 1;
      if (a.value === "value") return -1;
      if (b.value === "value") return 1;
      return a.label.localeCompare(b.label);
    });

    return [{ id: p.id, product, selectors: sorted }];
  });
}
